/**
 * Загрузка данных с перезапросом по событиям сервера.
 *
 * Библиотека кеширования здесь была бы избыточна: экранов немного, данные
 * маленькие, а обновление приходит потоком событий, а не по таймеру.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, apiGet } from './api.ts';
import { useLiveEvents, type LiveEvent } from './live.ts';

export interface Resource<T> {
  data: T | null;
  error: string | null;
  /** Первая загрузка: показываем скелет вместо содержимого. */
  loading: boolean;
  /** Фоновое обновление: содержимое уже есть, показываем лёгкий индикатор. */
  refreshing: boolean;
  unauthorized: boolean;
  reload: () => void;
}

export function useResource<T>(path: string | null, shouldReload?: (event: LiveEvent) => boolean): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [refreshing, setRefreshing] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    if (path === null) {
      setData(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setData((current) => {
      if (current === null) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      return current;
    });

    apiGet<T>(path, controller.signal)
      .then((result) => {
        if (cancelled) {
          return;
        }

        setData(result);
        setError(null);
        setUnauthorized(false);
      })
      .catch((cause: unknown) => {
        if (cancelled || (cause instanceof DOMException && cause.name === 'AbortError')) {
          return;
        }

        if (cause instanceof ApiError && cause.isUnauthorized) {
          setUnauthorized(true);
          setError(null);
          return;
        }

        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить данные');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [path, nonce]);

  useLiveEvents((event) => {
    if (shouldReload === undefined ? event.type !== 'connected' : shouldReload(event)) {
      reload();
    }
  });

  return { data, error, loading, refreshing, unauthorized, reload };
}
